import { useEffect, useRef, useState } from "react";
import { LiaHornbill, LiaSearchSolid } from "react-icons/lia";
import { MdClose, MdProductionQuantityLimits } from "react-icons/md";
import { dieMasterAPI } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Button from "../../common/Button";
import { BsSendFill } from "react-icons/bs";
import { DiEnvato } from "react-icons/di";
import { formatDateTime } from "../../utlis/formateDate";
import { STATUS_COLORS } from "../../utlis/constant";
import { RegistryHeader, Button as SharedButton, CalculationFooter } from "../../common/Shared";

const DieAllocation = () => {
  const user = JSON.parse(sessionStorage.getItem("user"));
  const userId = user?.userId;
  const [searchQuery, setSearchQuery] = useState("");
  const [isDiePreparedData, setIsDiePreparedData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("die-preparation");
  const [preparedPage, setPreparedPage] = useState(1);
  const [preparedHasMore, setPreparedHasMore] = useState(true);
  const [sendOvenData, setSendOvenData] = useState([]);
  const [sendOvenPage, setSendOvenPage] = useState(1);
  const [sendOvenHasMore, setSendOvenHasMore] = useState(true);
  const pageSize = 50;
  const tableScrollRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const fetchDiePreparationData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllDieAllocation(page, pageSize);

      setIsDiePreparedData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setPreparedHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const fetchSendtoovenData = async (page) => {
    try {
      setLoading(true);
      const res = await dieMasterAPI.getAllOvenIssuedData(page, pageSize);

      setSendOvenData((prev) => {
        return page === 1 ? res.data || [] : [...prev, ...(res.data || [])];
      });

      if (!res.data || res.data.length < pageSize) {
        setSendOvenHasMore(false);
      }
      isFetchingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "die-preparation") {
      setPreparedPage(1);
      setIsDiePreparedData([]);
      setPreparedHasMore(true);
    } else {
      setSendOvenPage(1);
      setSendOvenData([]);
      setSendOvenHasMore(true);
    }
  };

  useEffect(() => {
    if (activeTab === "die-preparation") fetchDiePreparationData(preparedPage);
  }, [preparedPage, activeTab]);

  useEffect(() => {
    if (activeTab === "send-to-oven") fetchSendtoovenData(sendOvenPage);
  }, [sendOvenPage, activeTab]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;

        if (activeTab === "die-preparation" && preparedHasMore) {
          setPreparedPage((p) => p + 1);
        }

        if (activeTab === "send-to-oven" && sendOvenHasMore) {
          setSendOvenPage((p) => p + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeTab, loading, preparedHasMore, sendOvenHasMore]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIsDiePreparedData(isDiePreparedData);
    } else {
      const filtered = isDiePreparedData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setIsDiePreparedData(filtered);
    }
  }, [searchQuery, isDiePreparedData]);

  const handleSendToOven = async () => {
    if (selectedIds.length === 0) {
      notify("Please select at least one die", "error");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        dieIds: selectedIds,
        createdBy: Number(userId),
      };

      await dieMasterAPI.sendToOven(payload);

      notify(`Successfully sent ${selectedIds.length} dies to Oven`, "success");

      setSelectedIds([]);
      setPreparedPage(1);
      setIsDiePreparedData([]);
      fetchDiePreparationData(1);

      setSendOvenPage(1);
      setSendOvenData([]);
      fetchSendtoovenData(1);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to send to oven";
      notify(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const ovenColumns = [
    { key: "dieNumber", label: "Die no" },
    { key: "currentWeight", label: "Current Weight" },
    {
      key: "status_Name",
      label: "Die Status",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location" },
    {
      key: "oven_Start_Datetime",
      label: "Oven Time",
      render: (value) => formatDateTime(value),
    },
  ];

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          className="cursor-pointer h-4 w-4"
          checked={
            isDiePreparedData.length > 0 &&
            selectedIds.length === isDiePreparedData.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              const allIds = isDiePreparedData.map((i) => i.dieId);
              setSelectedIds(allIds);
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      render: (_, row) => (
        <input
          type="checkbox"
          className="cursor-pointer h-4 w-4"
          checked={selectedIds.includes(row.dieId)}
          onChange={() => {
            const rowId = row.dieId;
            setSelectedIds((prev) =>
              prev.includes(rowId)
                ? prev.filter((id) => id !== rowId)
                : [...prev, rowId],
            );
          }}
        />
      ),
    },
    { key: "dieNumber", label: "Die no" },
    { key: "currentWeight", label: "Current Weight" },
    {
      key: "status_Name",
      label: "Status",
      render: (val) => {
        const color = STATUS_COLORS[val] || "bg-slate-100 text-slate-600";
        return (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            {val}
          </span>
        );
      },
    },
    { key: "location_Name", label: "Location" },
    { key: "oven_Start_Datetime", label: "Oven Time" },
  ];

  const renderDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl border-y border-slate-100">
        <div className="overflow-hidden rounded-xl border border-slate-200 ">
          <table className="w-full  text-[12px] border-collapse bg-white">
            <thead className="bg-slate-800 text-white uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">SO NO</th>
                <th className="px-4 py-2 text-center">Cut Length</th>
                <th className="px-4 py-2 text-right">Pcs</th>
                <th className="px-4 py-2 text-right">Weight (kg)</th>
                <th className="px-4 py-2 text-right">Order Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {row.details.map((item, i) => (
                <tr key={i} className="hover:bg-blue-50/40 transition-colors">
                  <td className="px-4 py-1.5 font-bold text-blue-600 underline cursor-pointer">
                    {item.order_No}
                  </td>
                  <td className="px-4 py-1.5 text-right text-slate-600">
                    {item.profile_Length}
                  </td>
                  <td className="px-4 py-1.5 text-right font-semibold text-slate-800">
                    {item.qty}
                  </td>
                  <td className="px-4 py-1.5 text-right text-emerald-600 font-bold">
                    {Number(item.qty_Weight).toFixed(3)}
                  </td>
                  <td className="px-4 py-1.5 text-right text-blue-600 font-bold">
                    {item.order_Status}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100/80 font-black border-t-2 border-slate-200">
              <tr>
                <td className="px-4 py-2 text-slate-500 uppercase">
                  Grand Total
                </td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2 text-right text-slate-900 text-sm">
                  {totalPcs}
                </td>
                <td className="px-4 py-2 text-right text-emerald-700 text-sm">
                  {totalWeight.toFixed(3)}
                </td>
                <td className="px-4 py-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderDieDetailTable = (row) => {
    if (!row.details || row.details.length === 0) {
      return (
        <div className="text-[11px] text-slate-400 p-3 italic bg-slate-50/50">
          No batch details available.
        </div>
      );
    }

    const totalPcs = row.details.reduce(
      (sum, item) => sum + (Number(item.qty) || 0),
      0,
    );
    const totalWeight = row.details.reduce(
      (sum, item) => sum + (Number(item.qty_Weight) || 0),
      0,
    );

    return (
      <div className="bg-slate-50 max-w-xl border-y border-slate-100">
        <div className="overflow-hidden rounded-xl border border-slate-200 ">
          <table className="w-full  text-[12px] border-collapse bg-white">
            <thead className="bg-slate-800 text-white uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-4 py-2 text-left">SO NO</th>
                <th className="px-4 py-2 text-center">Cut Length</th>
                <th className="px-4 py-2 text-right">Pcs</th>
                <th className="px-4 py-2 text-right">Weight (kg)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {row.details.map((item, i) => (
                <tr key={i} className="hover:bg-blue-50/40 transition-colors">
                  <td className="px-4 py-1.5 font-bold text-blue-600 underline cursor-pointer">
                    {item.order_No}
                  </td>
                  <td className="px-4 py-1.5 text-right text-slate-600">
                    {item.profile_Length}
                  </td>
                  <td className="px-4 py-1.5 text-right font-semibold text-slate-800">
                    {item.qty}
                  </td>
                  <td className="px-4 py-1.5 text-right text-emerald-600 font-bold">
                    {Number(item.qty_Weight).toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100/80 font-black border-t-2 border-slate-200">
              <tr>
                <td className="px-4 py-2 text-slate-500 uppercase">
                  Grand Total
                </td>
                <td className="px-4 py-2"></td>
                <td className="px-4 py-2 text-right text-slate-900 text-sm">
                  {totalPcs}
                </td>
                <td className="px-4 py-2 text-right text-emerald-700 text-sm">
                  {totalWeight.toFixed(3)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const totalPcs = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const pcs = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty) || 0),
        0,
      );
      return sum + pcs;
    }, 0);

  const totalWeight = isDiePreparedData
    .filter((item) => selectedIds.includes(item.dieId))
    .reduce((sum, item) => {
      const w = (item.details ?? []).reduce(
        (s, d) => s + (Number(d.qty_Weight) || 0),
        0,
      );
      return sum + w;
    }, 0)
    .toFixed(3);

  return (
    <div className="flex flex-col h-full bg-white font-plus">
      <RegistryHeader title="Die Allocation Matrix">
        {/* High-Density Tabs */}
        <div className="flex bg-gray-100 p-1 rounded-sm gap-1">
          <button
            onClick={() => handleTabChange("die-preparation")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "die-preparation"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Die Preparation
          </button>
          <button
            onClick={() => handleTabChange("send-to-oven")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "send-to-oven"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Send to Oven
          </button>
          <button
            onClick={() => handleTabChange("production-done")}
            className={`px-6 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === "production-done"
              ? "bg-[#3F80F7] text-white  #3F80F7]/20"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-200/50"
              }`}
          >
            Production Done
          </button>
        </div>

        {activeTab === "die-preparation" && (
          <>
            <div className="relative w-full sm:w-80 group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-[#3F80F7]">
                <LiaSearchSolid size={18} className="text-gray-400 transition-colors" />
              </div>
              <input
                type="text"
                className="block w-full pl-12 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-[11px] font-black uppercase tracking-[2px] focus:border-[#3F80F7] focus:bg-white focus:outline-none transition-all placeholder:text-gray-300"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <SharedButton
              disabled={selectedIds.length === 0 || loading}
              onClick={handleSendToOven}
              icon={BsSendFill}
              className="whitespace-nowrap"
            >
              {loading ? "Processing..." : `Issue To Oven [${selectedIds.length}]`}
            </SharedButton>
          </>
        )}
      </RegistryHeader>

      <div className="px-8 pt-0 pb-4 flex-1 overflow-hidden flex flex-col bg-white">
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-gray-200 bg-white flex-1"
        >
          {loading && <Loader centered />}
          {activeTab === "die-preparation" ? (
            <div className="min-w-max">
              <Table
                columns={columns.map(col => ({
                  ...col,
                  render: col.key === 'status_Name' ? (val) => {
                    const color = STATUS_COLORS[val] || "bg-gray-100 text-gray-600 border-gray-200";
                    const cleanColor = color.replace(/rounded-full|rounded-md|rounded-lg/g, 'rounded-none');
                    return (
                      <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[2px] border rounded-none ${cleanColor}`}>
                        {val}
                      </span>
                    );
                  } : col.render
                }))}
                data={isDiePreparedData}
                showActions={false}
                renderExpansion={renderDieDetailTable}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          ) : (
            <div className="min-w-max">
              <Table
                columns={ovenColumns.map(col => ({
                  ...col,
                  render: col.key === 'status_Name' ? (val) => {
                    const color = STATUS_COLORS[val] || "bg-gray-100 text-gray-600 border-gray-200";
                    const cleanColor = color.replace(/rounded-full|rounded-md|rounded-lg/g, 'rounded-none');
                    return (
                      <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[2px] border rounded-none ${cleanColor}`}>
                        {val}
                      </span>
                    );
                  } : col.render
                }))}
                data={sendOvenData}
                showActions={false}
                renderExpansion={renderDetailTable}
                tableClassName="text-[12px] font-bold"
              />
            </div>
          )}
        </div>
      </div>

      {/* Standardized Summary Footer */}
      {selectedIds.length > 0 && activeTab === "die-preparation" && (
        <CalculationFooter
          items={[
            {
              label: "SELECTED PCS",
              value: totalPcs.toLocaleString(),
              suffix: "PCS"
            },
            {
              label: "TOTAL WEIGHT",
              value: totalWeight,
              suffix: "KG",
              color: "text-[#3F80F7]"
            }
          ]}
        >
          <button
            onClick={() => setSelectedIds([])}
            className="px-8 py-2.5 text-[10px] font-black uppercase tracking-[3px] text-gray-400 hover:text-red-500 transition-colors border border-gray-200 hover:border-red-200 bg-white"
          >
            Clear
          </button>
          <button
            onClick={handleSendToOven}
            className="bg-[#3F80F7] text-white px-10 py-2.5 text-[10px] font-black uppercase tracking-[3px] hover:bg-gray-800 transition-all  #3F80F7]/20"
          >
            Issue To Oven
          </button>
        </CalculationFooter>
      )}
    </div>
  );
};

export default DieAllocation;

